import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const links = [
  { href: "#approach", label: "Approach" },
  { href: "#services", label: "Services" },
  { href: "#about", label: "About" },
  { href: "#faq", label: "FAQ" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed left-0 right-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled
          ? "bg-[#F7F5F0]/85 backdrop-blur-md border-b border-[#D5D1C9]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 sm:px-10">
        <a
          data-testid="nav-logo"
          href="#top"
          className="font-serif-display text-xl sm:text-[22px] tracking-tight text-[#1A1A18] flex items-baseline gap-2"
        >
          <span className="italic">DLG</span>
          <span className="hidden sm:inline text-[#5C5C59] text-sm font-sans-ui font-normal tracking-widest uppercase">
            Counseling &amp; Consulting
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-under font-sans-ui text-[13px] uppercase tracking-[0.22em] text-[#1A1A18]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            data-testid="nav-phone"
            href="tel:2096706799"
            className="font-sans-ui text-[12px] uppercase tracking-[0.22em] text-[#5C5C59] hover:text-[#1A1A18] transition-colors"
          >
            <Phone className="inline-block h-3 w-3 mr-2" strokeWidth={1.5} />
            209.670.6799
          </a>
          <a
            href="#book"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.2em] text-[#F7F5F0] hover:bg-[#1E382A] active:scale-[0.98] transition-[background-color,transform] duration-200"
          >
            Book Consultation
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center border border-[#D5D1C9] text-[#1A1A18]"
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#F7F5F0] border-t border-[#D5D1C9]"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className="font-serif-display text-3xl py-2 text-[#1A1A18]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#book"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-[#2C4C3B] px-5 py-3 text-[12px] uppercase tracking-[0.2em] text-[#F7F5F0]"
              >
                Book Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
